"use client";

import { createContext, useCallback, useContext, useRef, useState, ReactNode } from "react";
import { Button, Modal } from "./ui";

interface ConfirmOptions {
  title?: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => false);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({});
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setOpen(false);
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={open}
        onClose={() => settle(false)}
        title={opts.title || "Please confirm"}
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {opts.cancelLabel || "Cancel"}
            </Button>
            <Button variant={opts.danger ? "danger" : "primary"} onClick={() => settle(true)} autoFocus>
              {opts.confirmLabel || "Confirm"}
            </Button>
          </>
        }
      >
        <div className="text-sm text-gray-600">{opts.message}</div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
